import React, { useMemo } from "react";
import {
    AccountType,
    AccountSubtype,
    AccountClass,
} from "@common/constants/domains/accounting/accounts";
import {
    getAvailableAccountSubtypes,
    isValidAccountType,
    getAccountSubtypesForType,
} from "@common/constants/domains/accounting/accountTypeHelpers";
import { EntityProfileType } from "@common/types/entity";
import { useAccountingTabContext } from "@components/accounting/useAccountingTab";
import { BaseCellProps } from "./index";
import { DropdownCell } from "./DropdownCell";

interface AccountSubtypeCellProps extends BaseCellProps<string> {
    availableClassifications?: AccountClass[];
    legalType?: EntityProfileType | null;
}

export const AccountSubtypeCell: React.FC<AccountSubtypeCellProps> = ({
    row,
    rowIdx,
    availableClassifications,
    legalType: legalTypeProp,
    ...props
}) => {
    const { selectedEntity } = useAccountingTabContext();

    const accountType = row.accountType as AccountType | null;
    const accountClass = row.accountClass as AccountClass | null;

    const subtypeOptions = useMemo(() => {
        const legalType = legalTypeProp ?? selectedEntity?.legalType;

        if (!legalType) {
            return [];
        }

        let availableSubtypes: AccountSubtype[];
        if (!accountType || !isValidAccountType(accountType)) {
            const classesToFilter = accountClass
                ? [accountClass]
                : availableClassifications;

            availableSubtypes = getAvailableAccountSubtypes(
                legalType,
                classesToFilter,
            );
        } else {
            availableSubtypes = getAccountSubtypesForType(
                legalType,
                accountType,
            );
        }

        return availableSubtypes.map((subtype) => ({
            value: subtype,
            label: subtype,
        }));
    }, [
        legalTypeProp,
        selectedEntity?.legalType,
        accountType,
        accountClass,
        availableClassifications,
    ]);

    return (
        <DropdownCell
            {...props}
            rowIdx={rowIdx}
            row={row}
            options={subtypeOptions}
        />
    );
};
